import type { Language } from '@/types';
import { getTranslation } from '@/utils/translations';

class MobileMenu {
  private menuToggle: HTMLInputElement | null = null;
  private menu: HTMLElement | null = null;
  private overlay: HTMLElement | null = null;
  private currentLanguage: Language = 'fr';

  constructor() {
    this.init();
  }

  private init(): void {
    this.menuToggle = document.getElementById('menu-toggle') as HTMLInputElement;
    this.menu = document.getElementById('mobile-menu');
    
    if (!this.menuToggle || !this.menu) return;

    this.createOverlay();
    this.attachEventListeners();
  }

  private createOverlay(): void {
    // Create backdrop overlay
    this.overlay = document.createElement('div');
    this.overlay.className = `
      fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300
      opacity-0 pointer-events-none
    `;
    this.overlay.id = 'mobile-menu-overlay';
    
    document.body.appendChild(this.overlay);
  }

  private attachEventListeners(): void {
    if (!this.menuToggle) return;

    // Menu toggle change event
    this.menuToggle.addEventListener('change', this.handleToggle.bind(this));

    // Close menu when clicking overlay
    this.overlay?.addEventListener('click', this.closeMenu.bind(this));

    // Close menu when clicking menu links
    const menuLinks = this.menu?.querySelectorAll('a');
    menuLinks?.forEach(link => {
      link.addEventListener('click', this.closeMenu.bind(this));
    });

    // Close menu on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMenuOpen()) {
        this.closeMenu();
      }
    });

    // Handle window resize
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  private handleToggle(): void {
    if (!this.menuToggle) return;

    if (this.menuToggle.checked) {
      this.openMenu();
    } else {
      this.closeMenu();
    }
  }

  private openMenu(): void {
    if (!this.menu || !this.overlay) return;

    // Show overlay
    this.overlay.classList.remove('opacity-0', 'pointer-events-none');
    this.overlay.classList.add('opacity-100', 'pointer-events-auto');

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    // Animate menu items
    const menuItems = this.menu.querySelectorAll('li');
    menuItems.forEach((item, index) => {
      setTimeout(() => {
        item.style.transform = 'translateX(0)';
        item.style.opacity = '1';
      }, index * 50);
    });

    // Focus trap
    this.trapFocus();
  }

  private closeMenu(): void {
    if (!this.menuToggle || !this.menu || !this.overlay) return;

    // Uncheck toggle
    this.menuToggle.checked = false;

    // Hide overlay
    this.overlay.classList.remove('opacity-100', 'pointer-events-auto');
    this.overlay.classList.add('opacity-0', 'pointer-events-none');

    // Restore body scroll
    document.body.style.overflow = '';

    // Reset menu items animation
    const menuItems = this.menu.querySelectorAll('li');
    menuItems.forEach(item => {
      (item as HTMLElement).style.transform = 'translateX(-100%)';
      (item as HTMLElement).style.opacity = '0';
    });

    // Remove focus trap
    this.removeFocusTrap();
  }

  private trapFocus(): void {
    if (!this.menu) return;

    const focusableElements = this.menu.querySelectorAll(
      'a[href], button, [tabindex]:not([tabindex="-1"])'
    );
    
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTabKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleTabKey);
    
    // Store handler for cleanup
    (this.menu as HTMLElementWithTabHandler).tabHandler = handleTabKey;

    // Focus first element
    firstElement?.focus();
  }

  private removeFocusTrap(): void {
    if (!this.menu) return;

    const handler = (this.menu as HTMLElementWithTabHandler).tabHandler;
    if (handler) {
      document.removeEventListener('keydown', handler);
      delete (this.menu as HTMLElementWithTabHandler).tabHandler;
    }
  }

  private handleResize(): void {
    // Close menu on desktop breakpoint
    if (window.innerWidth >= 768 && this.isMenuOpen()) {
      this.closeMenu();
    }
  }

  private isMenuOpen(): boolean {
    return this.menuToggle?.checked || false;
  }

  public updateLanguage(language: Language): void {
    this.currentLanguage = language;
    // Update any dynamic text in menu if needed
    this.updateMenuLabels();
  }

  private updateMenuLabels(): void {
    if (!this.menu) return;

    // Update navigation links
    const navLinks = this.menu.querySelectorAll('[data-translate]');
    navLinks.forEach(link => {
      const key = link.getAttribute('data-translate');
      if (key) {
        link.textContent = getTranslation(this.currentLanguage, key);
      }
    });
  }

  public destroy(): void {
    // Clean up event listeners
    this.removeFocusTrap();
    
    // Remove overlay
    this.overlay?.remove();
    
    // Restore body scroll
    document.body.style.overflow = '';
  }
}

interface HTMLElementWithTabHandler extends HTMLElement {
  tabHandler?: (e: KeyboardEvent) => void;
}

export default MobileMenu;
